import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function VirtualListPage(): Node {
  return html`
    <div>
      <h1>Virtual List</h1>
      <p><code>VirtualList</code> renders only visible items in a scrollable list. Handles thousands of items without DOM overhead.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { html, createSignal } from 'onefold';
import { VirtualList } from 'onefold/virtual-list';

function UserList(): Node {
  const users = createSignal(Array.from({ length: 10000 }, (_, i) => ({
    id: i,
    name: \`User \${i}\`,
  })));

  return VirtualList({
    items: users,
    itemHeight: 48,
    containerHeight: 400,
    renderItem: (user) => html\`
      <div class="user-row" style="height:48px;display:flex;align-items:center">
        <span>\${user.name}</span>
      </div>
    \`,
  });
}`)}

      <h2>Options</h2>
      ${raw(`<table>
        <tr><th>Option</th><th>Type</th><th>Required</th><th>Description</th></tr>
        <tr><td><code>items</code></td><td>Signal&lt;T[]&gt;</td><td>Yes</td><td>Reactive list of all items.</td></tr>
        <tr><td><code>itemHeight</code></td><td>number</td><td>Yes</td><td>Fixed height of each item in pixels.</td></tr>
        <tr><td><code>containerHeight</code></td><td>number</td><td>Yes</td><td>Height of the scrollable viewport.</td></tr>
        <tr><td><code>renderItem</code></td><td>(item: T, index: number) => Node</td><td>Yes</td><td>Render function for each item.</td></tr>
        <tr><td><code>overscan</code></td><td>number</td><td>No</td><td>Extra items rendered above/below viewport (default: 5).</td></tr>
      </table>`)}

      ${Callout('VirtualList uses a fixed item height for O(1) scroll position calculations. Variable-height items are not supported.')}

      <h2>How It Works</h2>
      <ol>
        <li>Only items visible in the viewport (plus overscan) are rendered as DOM nodes.</li>
        <li>A spacer element maintains the correct scroll height.</li>
        <li>On scroll, items are recycled — removed from one end, added to the other.</li>
        <li>With 10,000 items and 400px viewport, only ~15 DOM nodes exist at any time.</li>
      </ol>

      <h2>With Filtering</h2>
      ${CodeBlock(`const allUsers = createSignal(generateUsers(10000));
const filter = createSignal('');

const filtered = createComputed(() =>
  allUsers().filter(u => u.name.toLowerCase().includes(filter().toLowerCase()))
);

function FilterableList(): Node {
  return html\`
    <div>
      <input value=\${() => filter()} oninput=\${(e: Event) => filter.set((e.target as HTMLInputElement).value)} placeholder="Search..." />
      \${VirtualList({
        items: filtered,
        itemHeight: 48,
        containerHeight: 400,
        renderItem: (user) => html\`<div class="row">\${user.name}</div>\`,
      })}
    </div>
  \`;
}`)}

      <h2>Try It</h2>
      <p>Scroll through 10,000 items — only ~15 DOM nodes exist at any time:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { VirtualList } from 'onefold/virtual-list';

function App() {
  // Generate 10,000 items
  const items = createSignal(
    Array.from({ length: 10000 }, (_, i) => ({
      id: i + 1,
      name: 'User ' + (i + 1),
      email: 'user' + (i + 1) + '@example.com',
      dept: ['Engineering', 'Design', 'Marketing', 'Sales', 'Support'][i % 5],
    }))
  );

  const count = () => items().length;

  return html\`
    <div>
      <h3>10,000 Users — Windowed</h3>
      <p style="font-size:12px;color:#666;margin-bottom:12px">
        \${() => count()} items in list. Only visible rows are in the DOM. Scroll to verify performance.
      </p>
      \${VirtualList({
        items,
        itemHeight: 44,
        height: 350,
        overscan: 4,
        renderRow: (user) => html\`
          <div style="display:flex;align-items:center;padding:0 16px;height:44px;border-bottom:1px solid #f1f5f9;font-size:13px">
            <div style="width:32px;height:32px;border-radius:50%;background:#e0e7ff;color:#4338ca;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:11px;margin-right:12px">
              \${user.name.charAt(5) || 'U'}
            </div>
            <div style="flex:1">
              <div style="font-weight:500">\${user.name}</div>
              <div style="font-size:11px;color:#94a3b8">\${user.email}</div>
            </div>
            <span style="font-size:11px;padding:2px 8px;border-radius:10px;background:#f1f5f9;color:#64748b">\${user.dept}</span>
          </div>
        \`,
      })}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'VirtualList — 10,000 Items', { height: 430 })}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/performance/code-splitting">Code Splitting</a> — lazy-load routes and heavy components</li>
        <li><a href="/core/signals">Signals</a> — the reactive primitives that drive VirtualList</li>
      </ul>
    </div>
  `;
}
